import { UnifiedUser } from "@/types/unified-user";
import { Button } from "@/components/ui/button";
import { X, Download, Tag, Shield, Users, Monitor } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface BulkActionToolbarProps {
  selectedUsers: UnifiedUser[];
  onClearSelection: () => void;
  onRefresh?: () => void;
}

export function BulkActionToolbar({
  selectedUsers,
  onClearSelection,
}: BulkActionToolbarProps) {
  const selectedCount = selectedUsers.length;
  const hasDirectoryUsers = selectedUsers.some(u => u.type === 'directory' || u.type === 'both');
  const hasConsoleUsers = selectedUsers.some(u => u.type === 'console' || u.type === 'both');
  const hasDevices = selectedUsers.some(u => u.deviceCount > 0);
  const totalDevices = selectedUsers.reduce((sum, u) => sum + u.deviceCount, 0);

  if (selectedCount === 0) return null;

  return (
    <div className="flex items-center justify-between p-4 bg-muted/50 border rounded-lg mb-4">
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className="text-sm">
          <Users className="w-3 h-3 mr-1" />
          {selectedCount} selected
        </Badge>
        {hasDevices && (
          <Badge variant="outline" className="text-sm">
            <Monitor className="w-3 h-3 mr-1" />
            {totalDevices} devices
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Export */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Export Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              Export User Details (CSV)
            </DropdownMenuItem>
            {hasDevices && (
              <DropdownMenuItem>
                Export with Devices (CSV)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem>
              Export Full Report (PDF)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Directory User Actions */}
        {hasDirectoryUsers && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Tag className="w-4 h-4 mr-2" />
                Directory Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Directory Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Tag className="w-4 h-4 mr-2" />
                Assign Tags
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Tag className="w-4 h-4 mr-2" />
                Remove Tags
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                Force Sync
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Console User Actions */}
        {hasConsoleUsers && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Shield className="w-4 h-4 mr-2" />
                Console Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Console Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Shield className="w-4 h-4 mr-2" />
                Assign Roles
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Shield className="w-4 h-4 mr-2" />
                Remove Roles
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Device Actions */}
        {hasDevices && (
          <Button variant="outline" size="sm">
            <Monitor className="w-4 h-4 mr-2" />
            View All Devices
          </Button>
        )}

        {/* Clear Selection */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
        >
          <X className="w-4 h-4 mr-2" />
          Clear
        </Button>
      </div>
    </div>
  );
}
